// Klub panel — Üzemeltetés (Figma OP_05, 05b, 05c, 05d): helyszín-összegző, függő kérelmek,
// nyitvatartás; helyszín szerkesztése; új helyszín kérelem (venueRequests) + visszaigazolás.
import React, { useEffect, useState } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { addDoc, collection, doc, getDocs, query, serverTimestamp, updateDoc, where, writeBatch } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Icon } from '../icons';
import { fmtDate, fmtInt, toDate } from '../data';
import { Badge, Button, Card, Field, Input, PageHeader, SectionTitle, Spinner, Toggle, useToast } from '../ui';
import { ClubInfo, DAY_NAMES, useBoards, useClub } from './clubData';

const SHORT = ['H', 'K', 'Sze', 'Cs', 'P', 'Szo', 'V'];
const shortH = (h: { from: string; to: string } | null) => (h ? `${h.from.replace(/:00$/, '')}–${h.to.replace(/:00$/, '')}` : 'Zárva');

export default function ClubOperationPage() {
  return (
    <Routes>
      <Route index element={<Overview />} />
      <Route path="edit" element={<EditVenue />} />
      <Route path="request" element={<NewRequest />} />
    </Routes>
  );
}

/* ---------- OP_05 ---------- */
function Overview() {
  const { club } = useClub();
  const { user } = useAuth();
  const nav = useNavigate();
  const boards = useBoards(club?.id);
  const [reqs, setReqs] = useState<{ id: string; name: string; location: string; at: Date | null; status: string }[] | null>(null);

  useEffect(() => {
    if (!user) return;
    getDocs(query(collection(db, 'venueRequests'), where('userId', '==', user.uid)))
      .then((s) => setReqs(s.docs.map((d) => {
        const v = d.data();
        return { id: d.id, name: String(v.venueName ?? ''), location: String(v.location ?? ''), at: toDate(v.createdAt), status: String(v.status ?? 'pending') };
      }).filter((r) => r.status === 'pending')))
      .catch(() => setReqs([]));
  }, [user]);

  if (!club || !boards) return <Spinner />;
  const price = boards.length ? Math.round(boards.reduce((s, b) => s + (b.priceBase / Math.max(1, b.priceMinutes)) * 60, 0) / boards.length) : 0;
  const lat = club.raw.lat ?? club.raw.latitude;
  const lng = club.raw.lng ?? club.raw.longitude;

  return (
    <div>
      <PageHeader title="Helyszínek kezelése" sub="Adja meg helyszínei adatait"
        right={<Button icon="Plus" onClick={() => nav('/club/operation/request')}>Új helyszín</Button>} />

      <Card className="!p-5">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="w-[96px] h-[72px] rounded-xl bg-[#1d232a] overflow-hidden shrink-0">
            {club.coverUrl && <img src={club.coverUrl} alt="" className="w-full h-full object-cover" />}
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="text-[18px] font-bold text-white">{club.name}</div>
            <div className="text-[13px] text-[#B8B8C3]">{[club.city, club.address].filter(Boolean).join(', ') || '—'}</div>
            <div className="flex gap-2 mt-2">
              <Badge tone="green">Aktív</Badge>
              {club.raw.verified === true ? <Badge tone="cyan">Hitelesített klub</Badge> : <Badge tone="gray">Nem hitelesített</Badge>}
            </div>
          </div>
          <Button kind="ghost" icon="PencilSimpleLine" onClick={() => nav('/club/operation/edit')}>Szerkesztés</Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
          {[
            ['Ár/óra', price ? `${fmtInt(price)} Ft` : '—'],
            ['Táblák', `${boards.length} tábla`],
            ['GPS', lat && lng ? `${Number(lat).toFixed(4)}, ${Number(lng).toFixed(4)}` : 'nincs megadva'],
          ].map(([k, v]) => (
            <div key={k} className="px-4 py-3 rounded-xl bg-[#1d232a]">
              <div className="text-[12px] text-[#8a8f99]">{k}</div>
              <div className="text-[15px] font-bold text-white mt-0.5">{v}</div>
            </div>
          ))}
        </div>
      </Card>

      {reqs && reqs.length > 0 && (
        <div className="mt-6 p-5 rounded-2xl border border-[#d9a514]/60" style={{ background: 'rgba(217,165,20,0.04)' }}>
          <SectionTitle right={<Badge tone="gold">{reqs.length}</Badge>}>Jóváhagyásra váró kérelmek</SectionTitle>
          {reqs.map((r) => (
            <div key={r.id} className="flex items-center gap-3 p-3 rounded-xl bg-[#1d232a] border border-[#31383D] mb-2">
              <Icon name="Clock" size={18} className="text-[#d9a514]" />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-white">{r.name}</div>
                <div className="text-[12px] text-[#8a8f99]">{r.location} · beküldve: {fmtDate(r.at)}</div>
              </div>
              <Badge tone="gold">Feldolgozás alatt</Badge>
            </div>
          ))}
        </div>
      )}

      <Card className="!p-5 mt-6">
        <SectionTitle>Nyitvatartás</SectionTitle>
        <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
          {SHORT.map((d, i) => (
            <div key={d} className="py-2.5 rounded-xl bg-[#1d232a] text-center">
              <div className="text-[11px] text-[#8a8f99]">{d}</div>
              <div className={`text-[13px] font-bold mt-0.5 ${club.hours[i] ? 'text-white' : 'text-[#8a8f99]'}`}>{shortH(club.hours[i])}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

/* ---------- OP_05b ---------- */
function EditVenue() {
  const { club } = useClub();
  const boards = useBoards(club?.id);
  if (!club || !boards) return <Spinner />;
  return <EditForm key={club.id} club={club} avgPrice={boards.length ? Math.round(boards[0].priceBase / Math.max(1, boards[0].priceMinutes) * 60) : 4500} boardIds={boards.map((b) => b.id)} />;
}

function EditForm({ club, avgPrice, boardIds }: { club: ClubInfo; avgPrice: number; boardIds: string[] }) {
  const toast = useToast();
  const nav = useNavigate();
  const r = club.raw as Record<string, string | undefined>;
  const [name, setName] = useState(club.name);
  const [zip, setZip] = useState(r.zip ?? '');
  const [city, setCity] = useState(club.city);
  const [street, setStreet] = useState(r.street ?? club.address);
  const [houseNo, setHouseNo] = useState(r.houseNo ?? '');
  const [price, setPrice] = useState(String(avgPrice));
  const [hours, setHours] = useState(club.hours.map((h) => (h ? { ...h } : null)));
  const [busy, setBusy] = useState(false);
  const lat = club.raw.lat ?? club.raw.latitude;
  const lng = club.raw.lng ?? club.raw.longitude;

  const save = async () => {
    if (!name.trim() || !city.trim()) { toast('A név és a város kötelező.', 'red'); return; }
    setBusy(true);
    try {
      const address = [street.trim(), houseNo.trim()].filter(Boolean).join(' ');
      await updateDoc(doc(db, 'clubs', club.id), {
        name: name.trim(), city: city.trim(), zip: zip.trim(), street: street.trim(), houseNo: houseNo.trim(), address,
        'schedule.hours': hours,
      });
      const p = Number(price) || 0;
      if (p && p !== avgPrice && boardIds.length) {
        // az óradíj az összes tábla 60 perces alapárát állítja
        const b = writeBatch(db);
        boardIds.forEach((id) => b.update(doc(db, 'clubs', club.id, 'boards', id), { priceBase: p, priceMinutes: 60 }));
        await b.commit();
      }
      toast('Módosítások mentve.');
      nav('/club/operation');
    } catch {
      toast('A mentés nem sikerült.', 'red');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader title="Helyszín szerkesztése" sub={club.name} right={<>
        <Button kind="ghost" onClick={() => nav('/club/operation')}>Mégse</Button>
        <Button icon="Check" disabled={busy} onClick={save}>Módosítások mentése</Button>
      </>} />
      <div className="grid grid-cols-1 xl:grid-cols-[1.2fr_1fr] gap-4 items-start">
        <Card className="!p-5">
          <SectionTitle>Alapadatok</SectionTitle>
          <div className="grid grid-cols-2 gap-x-3 gap-y-4">
            <Field label="Helyszín neve" className="col-span-2"><Input value={name} onChange={(e) => setName(e.target.value)} /></Field>
            <Field label="Irányítószám"><Input inputMode="numeric" value={zip} onChange={(e) => setZip(e.target.value)} /></Field>
            <Field label="Város"><Input value={city} onChange={(e) => setCity(e.target.value)} /></Field>
            <Field label="Utca"><Input value={street} onChange={(e) => setStreet(e.target.value)} /></Field>
            <Field label="Házszám"><Input value={houseNo} onChange={(e) => setHouseNo(e.target.value)} /></Field>
            <Field label="Ár/óra (Ft) — minden tábla alapára" className="col-span-2">
              <Input inputMode="numeric" value={fmtInt(Number(price) || 0)} onChange={(e) => setPrice(e.target.value.replace(/[^\d]/g, ''))} />
            </Field>
          </div>
          <div className="mt-4 h-[150px] rounded-xl bg-[#1d232a] border border-[#31383D] flex flex-col items-center justify-center gap-1">
            <Icon name="MapPin" size={26} className="text-[#00AFF2]" />
            <span className="text-[11px] text-[#8a8f99]">{lat && lng ? `GPS: ${Number(lat).toFixed(4)}, ${Number(lng).toFixed(4)}` : 'A GPS-koordinátát a mobilapp a címből számolja'}</span>
          </div>
        </Card>
        <Card className="!p-5">
          <SectionTitle>Nyitvatartás</SectionTitle>
          <div className="flex flex-col gap-2.5">
            {DAY_NAMES.map((d, i) => {
              const v = hours[i];
              const set = (nv: typeof v) => setHours((a) => a.map((x, j) => (j === i ? nv : x)));
              return (
                <div key={d} className="grid grid-cols-[84px_1fr_auto_1fr_auto] items-center gap-2.5 min-h-[40px]">
                  <span className="text-[13px] font-semibold text-white">{d}</span>
                  {v ? (
                    <>
                      <input type="time" value={v.from} onChange={(e) => set({ ...v, from: e.target.value })} className="h-[40px] px-3 rounded-xl bg-[#1d232a] border border-[#31383D] text-[14px] text-white [color-scheme:dark]" />
                      <span className="text-[#8a8f99]">–</span>
                      <input type="time" value={v.to} onChange={(e) => set({ ...v, to: e.target.value })} className="h-[40px] px-3 rounded-xl bg-[#1d232a] border border-[#31383D] text-[14px] text-white [color-scheme:dark]" />
                    </>
                  ) : <span className="col-span-3 text-[13px] italic text-[#8a8f99]">Zárva</span>}
                  <Toggle checked={Boolean(v)} onChange={(on) => set(on ? { from: '16:00', to: '23:00' } : null)} />
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}

/* ---------- OP_05c + 05d ---------- */
function NewRequest() {
  const { user } = useAuth();
  const nav = useNavigate();
  const toast = useToast();
  const [name, setName] = useState('');
  const [loc, setLoc] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const send = async () => {
    if (!name.trim() || !loc.trim() || !phone.trim()) { toast('Minden mező kitöltése kötelező.', 'red'); return; }
    setBusy(true);
    try {
      await addDoc(collection(db, 'venueRequests'), {
        userId: user?.uid ?? '', venueName: name.trim(), location: loc.trim(), phone: phone.trim(), status: 'pending', createdAt: serverTimestamp(),
      });
      setSent(true);
    } catch {
      toast('A kérelem nem ment el.', 'red');
    } finally {
      setBusy(false);
    }
  };

  const steps = ['Az admin 1–2 munkanapon belül ellenőrzi a kérelmet.', 'Jóváhagyás után a helyszín megjelenik az Üzemeltetés menüben.', 'Felveheted a táblákat, árakat és a nyitvatartást.'];

  if (sent) {
    return (
      <div>
        <PageHeader title="Üzemeltetői kérelem" sub="Új helyszín felvételéhez az admin jóváhagyása szükséges" />
        <Card className="!p-8 max-w-[560px] text-center">
          <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#2bc48a]/[0.14] text-[#2bc48a]"><Icon name="CheckCircle" size={30} /></span>
          <h2 className="mt-4 text-[20px] font-extrabold italic uppercase text-white">Kérelem elküldve</h2>
          <p className="mt-2 text-[14px] text-[#B8B8C3]">A(z) <b className="text-white">{name}</b> felvételi kérelmét megkaptuk. A döntésről értesítést küldünk.</p>
          <div className="mt-6"><Button onClick={() => nav('/club/operation')}>Vissza az Üzemeltetéshez</Button></div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Üzemeltetői kérelem" sub="Új helyszín felvételéhez az admin jóváhagyása szükséges" />
      <div className="grid grid-cols-1 xl:grid-cols-[1.4fr_1fr] gap-4 items-start">
        <Card className="!p-5">
          <SectionTitle>Helyszín adatai</SectionTitle>
          <div className="flex flex-col gap-4">
            <Field label="Létesítmény neve *"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="pl. Darts Club Budapest" /></Field>
            <Field label="Helyszín (cím) *"><Input icon="MapPin" value={loc} onChange={(e) => setLoc(e.target.value)} placeholder="pl. Budapest, Váci utca 1." /></Field>
            <Field label="Telefonszám *"><Input icon="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="pl. +36 30 123 4567" /></Field>
          </div>
          <div className="flex justify-end gap-3 mt-5">
            <Button kind="ghost" onClick={() => nav('/club/operation')}>Mégse</Button>
            <Button icon="PaperPlaneRight" disabled={busy} onClick={send}>Kérelem küldése</Button>
          </div>
        </Card>
        <Card className="!p-5">
          <SectionTitle>Mi történik ezután?</SectionTitle>
          {steps.map((s, i) => (
            <div key={s} className="flex items-start gap-3 mb-3">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#00AFF2]/[0.14] text-[#00AFF2] text-[12px] font-bold shrink-0">{i + 1}</span>
              <span className="text-[13px] text-[#B8B8C3]">{s}</span>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}
